import { Stack, Typography } from "@mui/material";
import { CustomCellDate, CrudLink } from "@/shared/components";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import { PagePaths } from "@/admin/shared/constants";
import { formatUsd } from "@/admin/shared/utils";
import { RefundButton } from "@/admin/features/refunds";
import { renderPaymentStatus } from "../domains";
import type { PaymentRow } from "./types";

export const getColumnsConfig = (): ColumnDefinition<PaymentRow>[] => [
  { name: "id", label: "ID", options: { display: false, filter: false } },
  {
    name: "amount",
    label: "Сумма",
    options: {
      sort: false,
      // Первая колонка это и есть вход в карточку платежа (все данные,
      // включая сырой ответ провайдера): ссылка на статусе одна не
      // читалась как ссылка.
      customBodyRender: (data: unknown) => {
        const payment = data as PaymentRow;
        return (
          <CrudLink id={payment.id} tab="details">
            {`${payment.amount.value} ${payment.amount.currency}`}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "amountUSD",
    label: "USD",
    options: {
      sort: false,
      customBodyRender: (value: unknown) =>
        formatUsd(value as number | null | undefined),
    },
  },
  {
    name: "ottkitFeeUSD",
    label: "Комиссия USD",
    options: {
      sort: false,
      customBodyRender: (value: unknown) =>
        formatUsd(value as number | null | undefined),
    },
  },
  {
    name: "organizationUSD",
    label: "Доход организации USD",
    options: {
      sort: false,
      customBodyRender: (value: unknown) =>
        formatUsd(value as number | null | undefined),
    },
  },
  {
    name: "provider",
    label: "Провайдер",
    options: {},
  },
  {
    name: "status",
    label: "Статус",
    options: {
      // Статус ведёт на детали платежа: отдельной колонки-ссылки в реестре
      // нет, а ?tab=details&id=<id> открывает диалог поверх списка.
      customBodyRender: (data: unknown) => {
        const payment = data as PaymentRow;
        return (
          <CrudLink id={payment.id} tab="details">
            {renderPaymentStatus(payment.status)}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "organization",
    label: "Организация",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const payment = data as PaymentRow;
        return (
          <CrudLink
            path={PagePaths.organizations}
            tab="details"
            id={payment.organizationId}
          >
            {payment.organization?.title ?? "—"}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "createdAt",
    label: "Дата",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => (
        <CustomCellDate date={value as string} />
      ),
    },
  },
  {
    name: "actions",
    label: "",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const payment = data as PaymentRow;
        if (payment.status !== "SUCCEEDED") return null;
        return (
          <RefundButton paymentId={payment.id} provider={payment.provider} />
        );
      },
    },
  },
];

export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Платежи",
  refetchList: "Обновить список",
  showTitle: "Детали",
  editItem: "Платежи",
});

export const getMobileColumnsConfig = (): DataListViewColumns<PaymentRow> => ({
  main: {
    title: "Платёж",
    sx: { flex: 1, minWidth: 0 },
    renderCell: (row) => (
      <Stack sx={{ minWidth: 0 }}>
        <CrudLink id={row.id} tab="details" noWrap>
          {row.amount.value} {row.amount.currency}
        </CrudLink>
        <Typography variant="body2" color="textSecondary" noWrap>
          {row.organization?.title ?? "—"}
        </Typography>
      </Stack>
    ),
  },
  status: {
    title: "Статус",
    sx: { width: 132, flexShrink: 0 },
    renderCell: (row) => renderPaymentStatus(row.status),
  },
});
